import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ApprovalLevel } from '../../../../../api/types/dasTickets.types';

interface TicketApprovalFlowProps {
  approvalHierarchy: ApprovalLevel[];
  ticketStatus?: string; // Overall ticket status (Approved, Rejected, etc.) for fallback
}

// Interface for grouped approval levels
interface GroupedApprovalLevel {
  level: number;
  levelName: string;
  approverRole: string;
  isFinalApprover: boolean;
  approvers: ApprovalLevel[];
  // Group status: determined by the overall status of approvers in this level
  groupStatus: string;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'approved':
      return '#10b981';
    case 'pre approved':
      return '#06b6d4'; // Cyan color for pre-approved
    case 'rejected':
      return '#ef4444';
    case 'pending':
      return '#f59e0b';
    case 'partial':
      return '#8b5cf6'; // Purple for partial (some approved, some pending)
    case 'skipped':
      return '#64748b';
    default:
      return '#94a3b8';
  }
};

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'approved':
      return <IconSystem.check size={16} />;
    case 'pre approved':
      return <IconSystem.checkCircle size={16} />;
    case 'rejected':
      return <IconSystem.x size={16} />;
    case 'pending':
      return <IconSystem.clock size={16} />;
    case 'partial':
      return <IconSystem.clock size={16} />;
    default:
      return <IconSystem.user size={16} />;
  }
};

const getApproverStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'approved':
      return '#10b981';
    case 'pre approved':
      return '#06b6d4';
    case 'rejected':
      return '#ef4444';
    case 'pending':
      return '#f59e0b';
    default:
      return '#94a3b8';
  }
};

export const TicketApprovalFlow: React.FC<TicketApprovalFlowProps> = ({ approvalHierarchy, ticketStatus }) => {
  if (!approvalHierarchy || approvalHierarchy.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          No approval hierarchy available for this ticket.
        </div>
      </div>
    );
  }

  // Determine if ticket is in a final state
  const ticketIsApproved = ticketStatus?.toLowerCase() === 'approved';
  const ticketIsRejected = ticketStatus?.toLowerCase() === 'rejected';

  // Helper to check if an approver was skipped due to pre-approval rejection
  const isSkippedDueToPreApprovalRejection = (comments: string | undefined): boolean => {
    return (
      comments?.includes('[Non-Final Approver Rejection]') ||
      comments?.includes('Non-Final Approver Rejection') ||
      false
    );
  };

  // Apply status override for final approvers when ticket is in final state
  // Also detect and handle steps skipped due to pre-approval rejection
  const processedHierarchy: ApprovalLevel[] = approvalHierarchy.map(level => {
    // Check if this level was skipped due to a pre-approval rejection
    if (isSkippedDueToPreApprovalRejection(level.comments)) {
      return {
        ...level,
        status: 'Skipped' as ApprovalLevel['status'],
        comments: 'Skipped due to pre-approval rejection',
      };
    }

    if (level.isFinalApprover && level.status === 'Pending' && (ticketIsApproved || ticketIsRejected)) {
      return {
        ...level,
        status: (ticketIsApproved ? 'Approved' : 'Rejected') as ApprovalLevel['status'],
      };
    }
    return level;
  });

  // Group approvers by level number
  const levelMap = new Map<string, GroupedApprovalLevel>();

  processedHierarchy.forEach(approver => {
    // Create a unique key combining level number and whether it's a final approver
    const levelKey = approver.isFinalApprover ? `final-${approver.level}` : `level-${approver.level}`;

    if (!levelMap.has(levelKey)) {
      levelMap.set(levelKey, {
        level: approver.level,
        levelName: approver.levelName,
        approverRole: approver.approverRole,
        isFinalApprover: approver.isFinalApprover || false,
        approvers: [],
        groupStatus: 'Pending',
      });
    }
    levelMap.get(levelKey)!.approvers.push(approver);
  });

  // Convert map to array and calculate group status
  const groupedLevels: GroupedApprovalLevel[] = [];
  levelMap.forEach(group => {
    // Determine group status based on individual approver statuses
    const statuses = group.approvers.map(a => a.status.toLowerCase());

    // Check if all approvers in this group were skipped
    if (statuses.every(s => s === 'skipped')) {
      group.groupStatus = 'Skipped';
    } else if (statuses.every(s => s === 'approved' || s === 'pre approved')) {
      group.groupStatus = statuses.includes('pre approved') ? 'Pre Approved' : 'Approved';
    } else if (statuses.some(s => s === 'rejected')) {
      group.groupStatus = 'Rejected';
    } else if (statuses.some(s => s === 'approved' || s === 'pre approved')) {
      group.groupStatus = 'Partial'; // Some approved, some pending
    } else if (statuses.some(s => s === 'skipped')) {
      group.groupStatus = 'Skipped';
    } else {
      group.groupStatus = 'Pending';
    }
    groupedLevels.push(group);
  });

  // Sort grouped levels: by level ascending, with Final Approvers always last
  groupedLevels.sort((a, b) => {
    if (a.isFinalApprover && !b.isFinalApprover) return 1;
    if (!a.isFinalApprover && b.isFinalApprover) return -1;
    return a.level - b.level;
  });

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Approval Hierarchy
        </h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Visual representation of the approval flow for this ticket
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {groupedLevels.map((group, index) => (
          <div key={index}>
            <div
              style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1rem',
                border: '2px solid ' + getStatusColor(group.groupStatus),
                position: 'relative',
              }}
            >
              {/* Level Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.75rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: getStatusColor(group.groupStatus),
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {group.levelName}
                      {/* Only show badge if levelName doesn't already indicate Final Approver */}
                      {group.isFinalApprover && !group.levelName.toLowerCase().includes('final approver') && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            padding: '0.125rem 0.5rem',
                            background: '#7c3aed',
                            color: 'white',
                            borderRadius: '0.25rem',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}
                        >
                          Final Approver
                        </span>
                      )}
                      {group.approvers.length > 1 && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            padding: '0.125rem 0.5rem',
                            background: '#6366f1',
                            color: 'white',
                            borderRadius: '0.25rem',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                          }}
                        >
                          {group.approvers.length} Approvers
                        </span>
                      )}
                    </div>
                    {group.approverRole && group.approverRole !== group.levelName && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{group.approverRole}</div>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem 0.75rem',
                    background: getStatusColor(group.groupStatus),
                    color: 'white',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  {getStatusIcon(group.groupStatus)}
                  {group.groupStatus}
                </div>
              </div>

              {/* Approvers List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {group.approvers.map((approver, approverIndex) => (
                  <div
                    key={approverIndex}
                    style={{
                      padding: '0.75rem',
                      background: 'var(--bg-primary)',
                      borderRadius: '0.375rem',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: getApproverStatusColor(approver.status),
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                          {approver.approverName}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          padding: '0.125rem 0.5rem',
                          borderRadius: '0.25rem',
                          background: getApproverStatusColor(approver.status) + '20',
                          color: getApproverStatusColor(approver.status),
                          textTransform: 'uppercase',
                        }}
                      >
                        {approver.status}
                      </span>
                    </div>

                    {/* Action Date */}
                    {approver.actionDate && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                        Action Date: {formatDate(approver.actionDate)}
                      </div>
                    )}

                    {/* Comments */}
                    {approver.comments && (
                      <div
                        style={{
                          marginTop: '0.5rem',
                          paddingTop: '0.5rem',
                          borderTop: '1px solid var(--border-color)',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          lineHeight: 1.4,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>Comments:</span> {approver.comments}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Connector Arrow */}
            {index < groupedLevels.length - 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0' }}>
                <div style={{ color: 'var(--text-muted)' }}>
                  <IconSystem.chevronDown size={20} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TicketApprovalFlow;
